import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { changeClassRoom } from '@core/actions/classRoom';
import {
  goToEngagementInsights,
  goToMaterials,
  openMaterials,
} from '@app/actions/navigation';
import markHomeSpaceVisitedFromHome from '@app/actions/home/markHomeSpaceVisitedFromHome';
import push from '@app/utils/routing';
import {
  trackHomeSpaceMaterialCardClicked,
  trackHomeSpaceNavigationCardClicked,
} from '../common';
import { SpaceWidgetCardData } from './SpaceWidget.types';

const switchSpace = async (
  dispatch: ReturnType<typeof useDispatch>,
  spaceId: string
) => {
  await dispatch(changeClassRoom(spaceId) as any);
};

export const useSpaceCardNavigation = (space: SpaceWidgetCardData) => {
  const dispatch = useDispatch();
  const isTitleNavigationInFlightRef = useRef(false);
  const isMaterialNavigationInFlightRef = useRef(false);

  const markVisitedFromHome = useCallback(() => {
    dispatch(markHomeSpaceVisitedFromHome(space.id, space.role));
  }, [dispatch, space.id, space.role]);

  const handleTitleClick = useCallback(
    async (event: React.MouseEvent | React.KeyboardEvent) => {
      event.stopPropagation();
      if (isTitleNavigationInFlightRef.current) {
        return;
      }
      isTitleNavigationInFlightRef.current = true;
      trackHomeSpaceNavigationCardClicked(space.id, space.role);
      markVisitedFromHome();
      try {
        await switchSpace(dispatch, space.id);
        push(`/dashboard/${space.id}/myspacenav`);
      } catch {
        isTitleNavigationInFlightRef.current = false;
      }
    },
    [dispatch, markVisitedFromHome, space.id, space.role]
  );

  const handleTitleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleTitleClick(event);
      }
    },
    [handleTitleClick]
  );

  const handleResumeClick = useCallback(async () => {
    if (isMaterialNavigationInFlightRef.current) {
      return;
    }
    isMaterialNavigationInFlightRef.current = true;
    trackHomeSpaceMaterialCardClicked(
      space.currentMaterialId,
      space.id,
      space.role
    );
    markVisitedFromHome();
    try {
      await switchSpace(dispatch, space.id);

      if (space.currentMaterialId) {
        dispatch(
          goToMaterials(space.id, space.currentMaterialId, { from: 'home' })
        );
        return;
      }

      if (space.role === 'student') {
        dispatch(goToMaterials(space.id, null, { from: 'home' }));
        return;
      }

      dispatch(openMaterials(space.id, { from: 'home' }));
    } catch {
      isMaterialNavigationInFlightRef.current = false;
    }
  }, [dispatch, markVisitedFromHome, space]);

  const handleEngagementClick = useCallback(
    async (event: React.MouseEvent | React.KeyboardEvent) => {
      event.stopPropagation();
      markVisitedFromHome();
      await switchSpace(dispatch, space.id);
      dispatch(goToEngagementInsights(space.id, null, { from: 'home' }));
    },
    [dispatch, markVisitedFromHome, space.id]
  );

  const handleEngagementKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleEngagementClick(event);
      }
    },
    [handleEngagementClick]
  );

  return {
    handleTitleClick,
    handleTitleKeyDown,
    handleResumeClick,
    handleEngagementClick,
    handleEngagementKeyDown,
  };
};
